import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useEffect } from "react";
import drEasyApi from "../Api/Customapi";
import { useParams } from "react-router-dom";

// Get the token once
export const getTokenBySlug = (slug) =>
  localStorage.getItem(`userTkn-${slug}`) ||
  sessionStorage.getItem(`userTkn-${slug}`);

export const useNotifications = () => {
  const { slug } = useParams();
  const token = getTokenBySlug(slug);

  return useQuery({
    queryKey: ["notifications", token],
    queryFn: async () => {
      const res = await drEasyApi.get(`/api/Notification/GetMyNotifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    },
    enabled: !!token,
    refetchOnWindowFocus: false,
    // Remove automatic refetch - we'll control this manually
    refetchInterval: false,
    refetchIntervalInBackground: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
};

export const useUnreadCount = () => {
  const { slug } = useParams();
  const token = getTokenBySlug(slug);
  const queryClient = useQueryClient();
  const prevUnreadCountRef = useRef(null);

  const query = useQuery({
    queryKey: ["unreadCount", token],
    queryFn: async () => {
      const res = await drEasyApi.get(`/api/Notification/GetUnreadCount`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data.unreadCount;
    },
    enabled: !!token,
    refetchInterval: 10000, // Check every 10 seconds (increased from 5 seconds)
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 0,
  });

  // Watch for changes in unread count and refetch notifications if changed
  useEffect(() => {
    const currentUnreadCount = query.data;
    
    if (currentUnreadCount !== undefined && 
        prevUnreadCountRef.current !== null && 
        currentUnreadCount !== prevUnreadCountRef.current) {
      
      console.log(`Unread count changed from ${prevUnreadCountRef.current} to ${currentUnreadCount}`);
      
      // Refetch notifications when unread count changes
      queryClient.invalidateQueries(["notifications", token]);
    }
    
    prevUnreadCountRef.current = currentUnreadCount;
  }, [query.data, token, queryClient]);

  return query;
};

export const useMarkAsRead = () => {
  const { slug } = useParams();
  const queryClient = useQueryClient();
  const token = getTokenBySlug(slug);

  return useMutation({
    mutationFn: (id) =>
      drEasyApi.put(`/api/Notification/MarkAsRead/${id}`, null, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      // Invalidate both queries to ensure consistency
      queryClient.invalidateQueries(["notifications", token]);
      queryClient.invalidateQueries(["unreadCount", token]);
    },
  });
};

export const useMarkAllAsRead = () => {
  const { slug } = useParams();
  const queryClient = useQueryClient();
  const token = getTokenBySlug(slug);

  return useMutation({
    mutationFn: () =>
      drEasyApi.put(`/api/Notification/MarkAllAsRead`, null, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      // Update the cache immediately for better UX
      queryClient.setQueryData(["unreadCount", token], 0);
      
      // Then invalidate to ensure fresh data
      queryClient.invalidateQueries(["notifications", token]);
      queryClient.invalidateQueries(["unreadCount", token]);
    },
  });
};

// New hook for opening notification
export const useOpenNotification = () => {
  const { slug } = useParams();
  const queryClient = useQueryClient();
  const token = getTokenBySlug(slug);

  return useMutation({
    mutationFn: (id) =>
      drEasyApi.put(`/api/Notification/OpenNotfication/${id}`, null, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      // Invalidate both queries to ensure consistency
      queryClient.invalidateQueries(["notifications", token]);
      queryClient.invalidateQueries(["unreadCount", token]);
    },
  });
};